/**
 * KINK3D Sizing Page Interactive Logic
 * Vanilla JS - no dependencies
 */

(function() {
  'use strict';

  // Guard against double-init: each sizing-* section may include this script,
  // and browsers run each <script src=...> tag even when the URL is identical.
  if (window.__sizingPageInit) return;
  window.__sizingPageInit = true;

  // ============================================
  // CONSTANTS
  // ============================================
  
  const CAGE_SIZES = [
    { size: 'Baby', min: 1.25, max: 2, range: '<2"', rangeCm: '<5 cm' },
    { size: 'N', min: 2, max: 2.5, range: '2-2.5"', rangeCm: '5-6.5 cm' },
    { size: 'N+', min: 2.5, max: 3, range: '2.5-3"', rangeCm: '6.5-7.5 cm' },
    { size: 'S', min: 3, max: 3.5, range: '3-3.5"', rangeCm: '7.5-9 cm' },
    { size: 'S+', min: 3.5, max: 4.25, range: '3.5-4.25"', rangeCm: '9-11 cm' },
    { size: 'R', min: 4.25, max: 5, range: '4.25-5"', rangeCm: '11-12.5 cm' },
    { size: 'R+', min: 5, max: 5.5, range: '5-5.5"', rangeCm: '12.5-14 cm' },
    { size: 'BFG', min: 5.5, max: 6, range: '5.5"-6"', rangeCm: '14-15 cm' }
  ];

  const SIZE_BOUNDARIES = [1.25, 2, 2.5, 3, 3.5, 4.25, 5, 5.5];
  const BOUNDARY_THRESHOLD = 0.15;
  const MIN_MEASUREMENTS = 6;

  // Return the display range string for a cage size in the user's current unit.
  function displayRange(sizeObj) {
    return state.unit === 'cm' ? sizeObj.rangeCm : sizeObj.range;
  }

  // ============================================
  // STATE
  // ============================================
  
  const state = {
    measurements: ['', '', '', '', '', ''],
    unit: 'inches',
    isCircumcised: null,
    showResult: false,
    selectedCageSize: null,   // currently unused — cage/style sections removed; may return
    selectedWidth: 'standard', // currently unused — cage/style sections removed; may return
    selectedStyle: 'cobra'     // currently unused — cage/style sections removed; may return
  };

  // ============================================
  // DOM HELPERS
  // ============================================
  
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  // ============================================
  // ACCORDION LOGIC
  // ============================================
  
  function initAccordion() {
    const headers = $$('.sizing-section-header');
    
    headers.forEach(header => {
      header.addEventListener('click', () => {
        const section = header.closest('.sizing-section');
        const content = $('.sizing-section-content', section);
        const isOpen = header.getAttribute('aria-expanded') === 'true';
        
        header.setAttribute('aria-expanded', !isOpen);
        content.dataset.open = !isOpen;
      });
    });
  }

  function openSection(sectionId) {
    const section = $(`#sizing-${sectionId}`);
    if (!section) return;
    
    const header = $('.sizing-section-header', section);
    const content = $('.sizing-section-content', section);
    
    header.setAttribute('aria-expanded', 'true');
    content.dataset.open = 'true';
    
    setTimeout(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  // Normalize a hash/anchor into an accordion section id, e.g.
  // "contact-bottom" or "sizing-contact-bottom" -> "contact-bottom".
  function sectionIdFromHash(hash) {
    return (hash || '').replace(/^#/, '').replace(/^sizing-/, '');
  }

  function initJumpLinks() {
    const jumpLinks = $$('.sizing-jump-link');

    jumpLinks.forEach(link => {
      link.addEventListener('click', () => {
        const sectionId = link.dataset.section;
        openSection(sectionId);
      });
    });

    // Let ordinary in-page links (e.g. one added inside the hero rich-text:
    // <a href="#contact-bottom">…</a>) open + scroll to a collapsed accordion,
    // which a native hash jump can't do on its own.
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href*="#"]');
      if (!a) return;

      // Only handle links that stay on this page.
      const rawHref = a.getAttribute('href') || '';
      const samePage = rawHref.startsWith('#') || a.pathname === window.location.pathname;
      if (!samePage || !a.hash) return;

      const sectionId = sectionIdFromHash(a.hash);
      if ($(`#sizing-${sectionId}`)) {
        e.preventDefault();
        openSection(sectionId);
        if (window.history.replaceState) window.history.replaceState(null, '', '#' + sectionId);
      }
    });

    // Handle URL hash on load (deep links like /pages/sizing#contact-bottom)
    if (window.location.hash) {
      openSection(sectionIdFromHash(window.location.hash));
    }
  }

  // ============================================
  // MEASUREMENT CALCULATOR
  // ============================================
  
  function getValidMeasurements() {
    return state.measurements
      .map(m => parseFloat(m))
      .filter(m => !isNaN(m) && m > 0);
  }

  function updateProgress() {
    const valid = getValidMeasurements();
    const count = valid.length;
    const pct = Math.min(100, (count / MIN_MEASUREMENTS) * 100);
    
    const fill = $('#progress-fill');
    const text = $('#progress-text');
    
    if (fill) {
      fill.style.width = pct + '%';
      fill.classList.toggle('complete', count >= MIN_MEASUREMENTS);
    }
    
    if (text) {
      text.textContent = `${count}/${MIN_MEASUREMENTS} minimum`;
      text.classList.toggle('complete', count >= MIN_MEASUREMENTS);
    }
    
    updateCalculateButton();
  }

  function updateCalculateButton() {
    const btn = $('#calculate-btn');
    if (!btn) return;
    
    const valid = getValidMeasurements();
    const count = valid.length;
    const canCalculate = count >= MIN_MEASUREMENTS && state.isCircumcised !== null;
    
    btn.disabled = !canCalculate;
    
    if (count < MIN_MEASUREMENTS) {
      btn.textContent = `Enter ${MIN_MEASUREMENTS - count} more measurement${MIN_MEASUREMENTS - count > 1 ? 's' : ''}`;
    } else if (state.isCircumcised === null) {
      btn.textContent = 'Select circumcised or uncircumcised';
    } else {
      btn.textContent = 'Calculate My Size';
    }
  }

  function renderMeasurementInputs() {
    const grid = $('#measurements-grid');
    if (!grid) return;
    
    // Clear existing inputs (keep the add button)
    const addBtn = $('#add-measurement');
    grid.innerHTML = '';
    
    state.measurements.forEach((value, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'sizing-measurement-wrapper';
      
      const input = document.createElement('input');
      input.type = 'text';
      input.inputMode = 'decimal';
      input.className = 'sizing-measurement-input';
      input.placeholder = `#${index + 1}`;
      input.value = value;
      input.dataset.index = index;
      
      input.addEventListener('input', (e) => {
        const sanitized = e.target.value.replace(/[^0-9.]/g, '');
        state.measurements[index] = sanitized;
        e.target.value = sanitized;
        state.showResult = false;
        hideResult();
        updateProgress();
        checkUnitWarningBanner();
      });

      const unitLabel = document.createElement('span');
      unitLabel.className = 'sizing-measurement-unit';
      unitLabel.textContent = state.unit === 'inches' ? 'in' : 'cm';

      wrapper.appendChild(input);
      wrapper.appendChild(unitLabel);

      grid.appendChild(wrapper);
    });
    
    // Re-add the add button
    const newAddBtn = document.createElement('button');
    newAddBtn.type = 'button';
    newAddBtn.className = 'sizing-add-measurement';
    newAddBtn.id = 'add-measurement';
    newAddBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg> Add';
    newAddBtn.addEventListener('click', () => {
      state.measurements.push('');
      state.showResult = false;
      hideResult();
      renderMeasurementInputs();
    });
    grid.appendChild(newAddBtn);
  }

  function calculateRecommendation() {
    const valid = getValidMeasurements();
    if (valid.length < MIN_MEASUREMENTS) return null;
    
    let values = [...valid].sort((a, b) => a - b);
    
    // Convert to inches if needed
    if (state.unit === 'cm') {
      values = values.map(v => v / 2.54);
    }
    
    // Take bottom third (minimum 2)
    const bottomCount = Math.max(2, Math.floor(values.length / 3));
    let bottomValues = values.slice(0, bottomCount);
    
    // Outlier detection
    if (bottomValues.length >= 2) {
      const gap = bottomValues[1] - bottomValues[0];
      if (gap > 0.5) {
        bottomValues = bottomValues.slice(1);
      }
    }
    
    const consistentSmallest = bottomValues.reduce((a, b) => a + b, 0) / bottomValues.length;
    const rounded = Math.round(consistentSmallest * 100) / 100;
    
    // Find primary size
    let primarySize = CAGE_SIZES.find(s => rounded >= s.min && rounded < s.max);
    if (!primarySize && rounded < 1.25) {
      // Below the smallest length cage — recommend Baby (the smallest length).
      primarySize = CAGE_SIZES[0];
    }
    if (!primarySize) {
      primarySize = CAGE_SIZES[CAGE_SIZES.length - 1];
    }
    // If primarySize is BFG and consistentSmallest > 6.125" (BFG max is 6"),
    // show a note that they may not fit *entirely* in any current KINK3D cage, and ask
    // whether they're sure they're measuring completely soft.
    
    // Check if near boundary
    let nearBoundary = null;
    let smallerSize = null;
    let largerSize = null;
    
    for (let i = 0; i < SIZE_BOUNDARIES.length; i++) {
      const boundary = SIZE_BOUNDARIES[i];
      if (Math.abs(rounded - boundary) <= BOUNDARY_THRESHOLD) {
        nearBoundary = boundary;
        const lowerIndex = CAGE_SIZES.findIndex(s => s.max === boundary);
        const upperIndex = CAGE_SIZES.findIndex(s => s.min === boundary);
        if (lowerIndex >= 0) smallerSize = CAGE_SIZES[lowerIndex];
        if (upperIndex >= 0) largerSize = CAGE_SIZES[upperIndex];
        break;
      }
    }
    
    return {
      value: rounded,
      displayValue: state.unit === 'cm' ? Math.round(rounded * 2.54 * 10) / 10 : rounded,
      bottomValues,
      primarySize,
      isBetweenSizes: nearBoundary !== null && smallerSize && largerSize,
      smallerSize,
      largerSize,
      isLarge: rounded > 6.125,
      isSmall: rounded < 0.5
    };
  }

  // Swap {smaller}/{larger}/{smaller_range}/{larger_range}/{value}/{size}/{range}
  // tokens in editable result copy with the computed values.
  function fillTokens(tpl, rec) {
    // Rich-text editors mangle tokens two ways: they HTML-encode the braces
    // ("&#123;smaller&#125;") and/or wrap them in stray markup
    // ("{<strong>smaller</strong>}"). Normalize both so the token matches.
    return (tpl || '')
      .replace(/&#0*123;|&#x0*7b;|&lcub;|&lbrace;/gi, '{')
      .replace(/&#0*125;|&#x0*7d;|&rcub;|&rbrace;/gi, '}')
      .replace(/\{[^{}]*\}/g, (m) => m.replace(/<[^>]+>/g, ''))
      .replace(/\{smaller_range\}/g, rec.smallerSize ? displayRange(rec.smallerSize) : '')
      .replace(/\{larger_range\}/g, rec.largerSize ? displayRange(rec.largerSize) : '')
      .replace(/\{smaller\}/g, rec.smallerSize ? rec.smallerSize.size : '')
      .replace(/\{larger\}/g, rec.largerSize ? rec.largerSize.size : '')
      .replace(/\{range\}/g, rec.primarySize ? displayRange(rec.primarySize) : '')
      .replace(/\{size\}/g, rec.primarySize ? rec.primarySize.size : '')
      .replace(/\{value\}/g, rec.displayValue)
      .replace(/\{unit\}/g, state.unit === 'cm' ? ' cm' : '"');
  }

  // Build a result "Shop" button from the editable URL + label. Returns '' when
  // no URL is configured (so the button is simply omitted). {size} in the label
  // is replaced with the size name.
  function shopButton(size, variant) {
    const S = window.sizingCalculatorStrings || {};
    const url = (S.shopUrl || '').trim();
    if (!url) return '';
    const label = (S.shopLabel || 'Shop {size}').replace(/\{size\}/g, size);
    return `<a href="${url}" class="sizing-btn sizing-btn-${variant} sizing-result-shop">${label}</a>`;
  }

  function shopActions(buttons) {
    const html = buttons.map((b) => shopButton(b.size, b.variant)).join('');
    return html ? `<div class="sizing-result-actions">${html}</div>` : '';
  }

  function showResult() {
    const rec = calculateRecommendation();
    if (!rec) return;
    
    const result = $('#calculator-result');
    if (!result) return;
    
    const unitLabel = state.unit === 'inches' ? '"' : ' cm';

    const measurementLabel = (window.sizingCalculatorStrings && window.sizingCalculatorStrings.measurementLabel) ||
      "Your most consistent smallest measurement (approximately):";
    let html = `
      <div class="sizing-result-measurement">
        <p class="sizing-result-label">${measurementLabel}</p>
        <p class="sizing-result-value">${rec.displayValue}${unitLabel}</p>
      </div>
    `;
    
    if (rec.isBetweenSizes) {
      const S = window.sizingCalculatorStrings || {};
      const betweenTitle = fillTokens(S.betweenTitle || "You're between {smaller} and {larger}", rec);
      const betweenIntro = fillTokens(S.betweenIntro || "<p>Your measurement ({value}{unit}) lands right on the border between these two sizes.</p>", rec);
      const betweenBody = fillTokens(state.isCircumcised
        ? (S.betweenCirc || "<p><strong>Our recommendation:</strong> Most people prefer their cage snug, so we'd lean toward a KINK3D <strong>{smaller}</strong> ({smaller_range}) length cage.</p><p>That said, it really could go either way. If you tend to run larger or prefer a bit more room, a KINK3D {larger} ({larger_range}) length cage would also work.</p>")
        : (S.betweenUncut || "<p><strong>What will likely fit best depends on your foreskin:</strong></p><ul><li>If your foreskin sticks out a lot and is sensitive, you might want a KINK3D <strong>{larger}</strong> ({larger_range}) cage to give it room.</li><li>If it doesn't go too far past the tip, or you don't mind it sticking out and potentially rubbing on your clothes, a KINK3D <strong>{smaller}</strong> ({smaller_range}) cage will likely work.</li></ul>"), rec);
      html += `
        <div class="sizing-result-between">
          <div class="sizing-result-icon sizing-result-icon-warning">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
          </div>
          <div class="sizing-result-content">
            <p class="sizing-result-title">${betweenTitle}</p>
            <div class="sizing-result-between-copy">${betweenIntro}${betweenBody}</div>
            ${shopActions([
              { size: rec.smallerSize.size, variant: 'primary' },
              { size: rec.largerSize.size, variant: 'secondary' }
            ])}
          </div>
        </div>
      `;
    } else {
      const hasEdgeWarning = rec.isLarge || rec.isSmall;
      html += `
        <div class="sizing-result-clear">
          <div class="sizing-result-icon ${hasEdgeWarning ? 'sizing-result-icon-warning' : 'sizing-result-icon-success'}">
            ${hasEdgeWarning
              ? `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`
              : `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>`
            }
          </div>
          <div class="sizing-result-content">
            <p class="sizing-result-label">Recommended cage length:</p>
            <p class="sizing-result-size">${rec.primarySize.size}</p>
            <p class="sizing-result-range">For flaccid length ${displayRange(rec.primarySize)}</p>
            ${rec.isSmall ? `
              <div class="sizing-result-small">${fillTokens((window.sizingCalculatorStrings && window.sizingCalculatorStrings.tooSmallNote) || '<p>Your smallest consistent flaccid length is below our recommended length for a KINK3D Baby cage. This might mean that you\'ll have some issues with alignment, or keeping your shaft inside of the cage, especially if you don\'t wear some kind of chastity strap. <a href="#contact">Contact us</a> for advice and recommendations.</p>', rec)}</div>
            ` : ''}
            ${rec.isLarge ? `
              <div class="sizing-result-large">${fillTokens((window.sizingCalculatorStrings && window.sizingCalculatorStrings.tooLargeNote) || "<p>If these measurements are when you're completely soft, you may not fit entirely in a KINK3D cage.</p><p>Please make sure you are <strong>completely</strong> soft. If you are and you still want to try, the Viper BFG Wide is our roomiest cage.</p>", rec)}</div>
            ` : ''}
            ${shopButton(rec.primarySize.size, 'primary')}
          </div>
        </div>
      `;
    }

    // Uncut note — shared across all result paths
    if (!state.isCircumcised) {
      html += `
        <div class="sizing-result-uncut-note">${(window.sizingCalculatorStrings && window.sizingCalculatorStrings.uncutNote) || "<p>💡 Since you're uncircumcised, double-check that the head ring width won't squeeze your foreskin. Some uncut wearers prefer the Wide version for extra room.</p>"}</div>
      `;
    }

    html += `
      <button type="button" class="sizing-recalculate" id="recalculate-btn">← Adjust measurements and recalculate</button>
    `;

    result.innerHTML = html;
    result.style.display = 'block';
    
    // Hide calculate button
    const calcBtn = $('#calculate-btn');
    if (calcBtn) calcBtn.style.display = 'none';
    
    // Bind recalculate
    const recalcBtn = $('#recalculate-btn');
    if (recalcBtn) {
      recalcBtn.addEventListener('click', () => {
        state.showResult = false;
        hideResult();
      });
    }
    
    // Shop buttons navigate natively via their href (set from the editable URL).

    populateContactForms();
  }

  // overwrites textarea for values on every state change from calculator.
  function populateContactForms() {
    const textareas = $$('.sizing-contact-details');
    if (textareas.length === 0) return;

    const measurements = state.measurements.filter(m => m !== '' && !isNaN(parseFloat(m)));

    // No measurements yet — leave the box empty so its placeholder shows instead
    // of a lone "Unit: inches" line.
    if (measurements.length === 0) {
      textareas.forEach(ta => { ta.value = ''; });
      return;
    }

    const unitName = state.unit === 'inches' ? 'inches' : 'cm';
    const unitLbl = state.unit === 'inches' ? '"' : ' cm';

    let summary = `Unit: ${unitName}\n`;
    summary += `Measurements: ${measurements.map(m => m + unitLbl).join(', ')}\n`;
    if (state.isCircumcised !== null) {
      summary += `Circumcised: ${state.isCircumcised ? 'yes' : 'no'}\n`;
    }

    if (state.showResult) {
      const rec = calculateRecommendation();
      if (rec) {
        if (rec.isBetweenSizes) {
          summary += `Calculated: between ${rec.smallerSize.size} and ${rec.largerSize.size} (${rec.value}")\n`;
        } else {
          summary += `Calculated: ${rec.primarySize.size} (${rec.value}")\n`;
          if (rec.isLarge) summary += `Note: above BFG max (>6")\n`;
        }
      }
    }

    textareas.forEach(ta => { ta.value = summary; });
  }

  function hideResult() {
    const result = $('#calculator-result');
    const calcBtn = $('#calculate-btn');

    if (result) result.style.display = 'none';
    if (calcBtn) calcBtn.style.display = 'block';

    updateCalculateButton();
    populateContactForms();
  }

  function checkUnitWarningBanner() {
    const existing = $('#sizing-unit-warning-banner');
    if (existing) existing.remove();

    const entered = state.measurements.map(m => parseFloat(m)).filter(n => !isNaN(n) && n > 0);
    if (entered.length === 0) return;

    const grid = $('#measurements-grid');
    if (!grid) return;

    let type = null;
    if (state.unit === 'inches' && entered.some(n => n >= 8)) type = 'to-cm';
    else if (state.unit === 'cm' && entered.some(n => n >= 30)) type = 'mm-to-cm';
    if (!type) return;

    const banner = document.createElement('div');
    banner.id = 'sizing-unit-warning-banner';
    banner.className = 'sizing-unit-warning-banner';

    const msg = document.createElement('span');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sizing-unit-warning-banner-btn';

    if (type === 'to-cm') {
      btn.textContent = 'Convert to cm?';
      btn.addEventListener('click', () => {
        $$('.sizing-unit-btn').forEach(b => b.classList.toggle('active', b.dataset.unit === 'cm'));
        state.unit = 'cm';
        $$('.sizing-measurement-unit').forEach(el => { el.textContent = 'cm'; });
        banner.remove();
        state.showResult = false;
        hideResult();
        updateProgress();
        checkUnitWarningBanner();
      });
    } else {
      btn.textContent = 'Convert from mm to cm';
      btn.addEventListener('click', () => {
        state.measurements = state.measurements.map(m => {
          const n = parseFloat(m);
          return (!isNaN(n) && n >= 30) ? String(Math.round((n / 10) * 10) / 10) : m;
        });
        banner.remove();
        state.showResult = false;
        hideResult();
        renderMeasurementInputs();
        updateProgress();
      });
    }

    banner.appendChild(msg);
    banner.appendChild(btn);
    grid.parentNode.insertBefore(banner, grid);
  }

  function initCalculator() {
    // Unit toggle
    $$('.sizing-unit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('.sizing-unit-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        state.unit = btn.dataset.unit;
        $$('.sizing-measurement-unit').forEach(el => { el.textContent = state.unit === 'inches' ? 'in' : 'cm'; });
        state.showResult = false;
        hideResult();
        checkUnitWarningBanner();
      });
    });
    
    // Circumcised buttons
    $$('.sizing-circumcised-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('.sizing-circumcised-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        state.isCircumcised = btn.dataset.value === 'true';
        state.showResult = false;
        hideResult();
        updateCalculateButton();
      });
    });
    
    // Calculate button
    const calcBtn = $('#calculate-btn');
    if (calcBtn) {
      calcBtn.addEventListener('click', () => {
        state.showResult = true;
        showResult();
      });
    }

    // Clear button — reset the measurement entry back to a blank slate
    const clearBtn = $('#clear-measurements');
    if (clearBtn) {
      clearBtn.addEventListener('click', clearCalculator);
    }

    // Initial render
    renderMeasurementInputs();
    updateProgress();
  }

  function clearCalculator() {
    state.measurements = ['', '', '', '', '', ''];
    state.isCircumcised = null;
    state.showResult = false;

    $$('.sizing-circumcised-btn').forEach(b => b.classList.remove('selected'));

    const banner = $('#sizing-unit-warning-banner');
    if (banner) banner.remove();

    hideResult();              // hides result, restores Calculate button, refreshes contact summary
    renderMeasurementInputs(); // rebuild the (now empty) input grid
    updateProgress();
    persistState();            // save the cleared state so a refresh doesn't restore old numbers
  }

  // ============================================
  // CAGE SIZE SELECTION — currently unused; cage/style sections removed; may return
  // ============================================

  function updateCageSelection() {
    const summary = $('#cage-selection');
    const text = $('#cage-selection-text');
    const cobraBtn = $('#shop-cobra-btn');
    const viperBtn = $('#shop-viper-btn');
    
    if (!state.selectedCageSize) {
      if (summary) summary.style.display = 'none';
      return;
    }
    
    if (summary) summary.style.display = 'flex';
    
    const widthLabel = state.selectedWidth.charAt(0).toUpperCase() + state.selectedWidth.slice(1);
    if (text) text.textContent = `${state.selectedCageSize} ${widthLabel}`;
    
    if (cobraBtn) cobraBtn.textContent = `Shop Cobra ${state.selectedCageSize}`;
    if (viperBtn) viperBtn.textContent = `Shop Viper ${state.selectedCageSize}`;
  }

  function initCageSizing() {
    // Size cards
    $$('.sizing-size-card').forEach(card => {
      card.addEventListener('click', () => {
        $$('.sizing-size-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        state.selectedCageSize = card.dataset.size;
        updateCageSelection();
      });
    });
    
    // Width cards
    $$('.sizing-width-card').forEach(card => {
      card.addEventListener('click', () => {
        $$('.sizing-width-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        state.selectedWidth = card.dataset.width;
        updateCageSelection();
      });
    });
  }

  // ============================================
  // STYLE SELECTION — currently unused; cage/style sections removed; may return
  // ============================================

  function initStyleSelection() {
    $$('.sizing-style-card').forEach(card => {
      card.addEventListener('click', () => {
        $$('.sizing-style-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        state.selectedStyle = card.dataset.style;
      });
    });
  }

  // ============================================
  // PERSISTENCE (sessionStorage — clears when the tab/browser closes)
  // ============================================

  const STORAGE_KEY = 'kink3d_sizing_state_v1';

  // First non-empty value among matching fields (the page can have more than
  // one contact form, e.g. a top and bottom "contact" section).
  function firstFilledValue(selector) {
    const el = $$(selector).find(e => e.value && e.value.trim() !== '');
    return el ? el.value : '';
  }

  function persistState() {
    try {
      const data = {
        measurements: state.measurements,
        unit: state.unit,
        isCircumcised: state.isCircumcised,
        showResult: state.showResult,
        selectedCageSize: state.selectedCageSize,   // currently unused — may return
        selectedWidth: state.selectedWidth,         // currently unused — may return
        selectedStyle: state.selectedStyle,         // currently unused — may return
        contact: {
          email: firstFilledValue('.sizing-contact-email'),
          message: firstFilledValue('.sizing-contact-message')
        }
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      /* sessionStorage unavailable (private mode / quota) — fail silently */
    }
  }

  function clearPersistedState() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  let persistTimer = null;
  function schedulePersist() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(persistState, 250);
  }

  function attachPersistence() {
    // Delegate on each section root so dynamically re-rendered inputs are covered.
    $$('.sizing-page-section').forEach(root => {
      root.addEventListener('input', schedulePersist);
      root.addEventListener('click', schedulePersist);
    });
  }

  // Load saved state into `state` and the contact fields. Returns true if anything was restored.
  function restoreState() {
    // After a successful submission the page reloads with this marker — clear and start fresh.
    if ($('.sizing-contact-submitted')) {
      clearPersistedState();
      return false;
    }

    let data;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      data = raw ? JSON.parse(raw) : null;
    } catch (e) {
      data = null;
    }
    if (!data) return false;

    if (Array.isArray(data.measurements) && data.measurements.length) state.measurements = data.measurements;
    if (data.unit) state.unit = data.unit;
    if (typeof data.isCircumcised === 'boolean') state.isCircumcised = data.isCircumcised;
    if (data.selectedCageSize) state.selectedCageSize = data.selectedCageSize; // currently unused — may return
    if (data.selectedWidth) state.selectedWidth = data.selectedWidth;         // currently unused — may return
    if (data.selectedStyle) state.selectedStyle = data.selectedStyle;         // currently unused — may return
    state.showResult = !!data.showResult;

    if (data.contact) {
      if (data.contact.email) $$('.sizing-contact-email').forEach(el => { el.value = data.contact.email; });
      if (data.contact.message) $$('.sizing-contact-message').forEach(el => { el.value = data.contact.message; });
    }

    return true;
  }

  // Sync the restored `state` onto the rendered controls (tabs, toggles, cards, result).
  function applyRestoredUI() {
    $$('.sizing-unit-btn').forEach(b => b.classList.toggle('active', b.dataset.unit === state.unit));
    $$('.sizing-measurement-unit').forEach(el => { el.textContent = state.unit === 'inches' ? 'in' : 'cm'; });

    if (state.isCircumcised !== null) {
      $$('.sizing-circumcised-btn').forEach(b => {
        b.classList.toggle('selected', (b.dataset.value === 'true') === state.isCircumcised);
      });
    }

    // Cage/style restore — currently unused; sections removed; may return
    if (state.selectedCageSize) {
      $$('.sizing-size-card').forEach(c => c.classList.toggle('selected', c.dataset.size === state.selectedCageSize));
    }
    $$('.sizing-width-card').forEach(c => c.classList.toggle('selected', c.dataset.width === state.selectedWidth));
    $$('.sizing-style-card').forEach(c => c.classList.toggle('selected', c.dataset.style === state.selectedStyle));

    updateCageSelection();

    if (state.showResult && calculateRecommendation()) {
      showResult();
    } else {
      state.showResult = false;
      populateContactForms();
    }
  }

  // ============================================
  // INIT
  // ============================================

  // After a successful contact post the page reloads with this marker, but the
  // success message lives inside an accordion panel that's collapsed by default.
  // Open that panel and scroll to it so the thank-you message is visible.
  function revealSubmittedSection() {
    const marker = $('.sizing-contact-submitted');
    if (!marker) return;
    const section = marker.closest('.sizing-section');
    if (section && section.dataset.section) openSection(section.dataset.section);
  }

  function init() {
    const restored = restoreState();
    initAccordion();
    initJumpLinks();
    initCalculator();
    initCageSizing();
    initStyleSelection();
    if (restored) applyRestoredUI();
    attachPersistence();
    revealSubmittedSection();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
